import { faChevronDown } from '@awesome.me/kit-eee5f32034/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Head, Link } from '@inertiajs/react';
import { isDarkMode } from '@onwardtravelsolutions/package-react/Utilities/browser';
import { Card, Quote, Rule, Section, TestimonialCard } from '../Components';
import { Banner, BannerHeader, BannerText } from '../Components/Banners';
import { PrimaryButton } from '../Components/Buttons';
import { NoticeErrorBoundary } from '../Components/ErrorBoundaries';
import { ContactForm } from '../Components/Forms';
import { SplitBlock, SplitBlockHeader } from '../Components/SplitBlocks';
import { ReCaptcha } from '../Types';
import { TrustpilotReviews, TrustpilotScore } from '../Components/Trustpilot';

const testimonials = [
    {
        author: 'Terrence, Customer',
        content:
            'First class service. On time, competitively priced, excellent communication. Have used OTS in the past & will certainly use them again.',
    },
    {
        author: 'H. Banks, Customer',
        content:
            'Always use this company and have not had any problems. Our recent trip back from Stansted with Chris was excellent.',
    },
    {
        author: 'S. Thompson, Customer',
        content:
            'Excellent service. The OTS website was easy to use; even from abroad. An instant quote was provided and there were prompt email updates.',
    },
];

const products = [
    {
        title: 'Online Booking Software',
        image: {
            src: '/img/tech/php-logo.svg',
            alt: 'PHP Logo',
            className: 'dark:invert',
        },
        content:
            'A range of modern booking applications with a strong focus on ease of use for both domestic and international users.',
    },
    {
        title: 'Geolocation API',
        image: { src: '/img/tech/laravel-logo.svg', alt: 'Laravel Logo' },
        content:
            'A flexible PAF-based Geolocation service supporting our various public and internal applications.',
    },
    {
        title: 'Operator Dashboard',
        image: {
            src: '/img/tech/php-logo.svg',
            alt: 'PHP Logo',
            className: 'dark:invert',
        },
        content:
            'An internal operations dashboard facilitating the management and taking of customer bookings.',
    },
    {
        title: 'Drivers Portal',
        image: { src: '/img/tech/reactjs-logo.svg', alt: 'React Logo' },
        content:
            'A highly focused management portal with live updating tendering, driver management and invoicing capabilities.',
    },
    {
        title: 'Routing Engine',
        image: { src: '/img/tech/python-logo.svg', alt: 'Python Logo' },
        content:
            'An efficient high-throughput routing engine, used for real-time route, fare and fees calculation.',
    },
    {
        title: '...And Many More',
        content:
            'Our projects are varied & extensive, contact us now to discuss how your skills might contribute to our collective growth.',
    },
];

type HomeProps = {
    recaptcha: ReCaptcha;
    sites: {
        public: {
            url: string;
        };
    };
};

export default function Home({ sites, recaptcha }: HomeProps) {
    return (
        <>
            <Head>
                <title>Transfer Booking Experts</title>
                <meta
                    name='description'
                    content='We are a UK based business specialising in optimising private hire travel through innovative technological solutions.'
                />
            </Head>

            <Banner className='sm:py-auto pt-20 pb-5 h-auto sm:h-screen'>
                <BannerHeader>
                    Airport Transfer
                    <br /> Experts In The UK
                </BannerHeader>
                <Rule className='mx-auto w-1/2 max-w-60' />
                <BannerText>
                    Working with thousands of drivers & serving{' '}
                    <strong className='some-test:bg-red-500'>
                        over a million customers
                    </strong>
                    , Onward Travel Solutions have provided top quality transfer
                    booking services for over{' '}
                    <strong>{new Date().getFullYear() - 2006} years</strong>
                </BannerText>
                <PrimaryButton
                    href={`${sites.public.url}/Contact`}
                    title='Book A Journey'
                    className='w-full max-w-md'
                    target='_blank'
                >
                    Make A Booking
                </PrimaryButton>
                <TrustpilotScore />
                <div className='block'>
                    <Link href='#our-vision' title='Scroll To Vision Section'>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className='m-auto mt-10 text-primary animate-bounce'
                            fontSize={35}
                        />
                    </Link>
                </div>
            </Banner>

            <SplitBlock
                id='our-vision'
                path='hero-4-seatbelt'
                alt='Airport Taxi Passenger'
                className='[&>div]:md:flex-row-reverse'
            >
                <SplitBlockHeader>Our Vision</SplitBlockHeader>
                <p>
                    Our aim is to become the most prominent airport transfer
                    company in the UK. We've achieved steady growth since our
                    inception in 2006 and continue to make major strides
                    (technologically and operationally) towards this goal.
                </p>
                <p>Our plan to realise this objective includes:</p>
                <ul>
                    <li>
                        Implementing modern technology to reduce{' '}
                        <em>"dead miles"</em> to save both costs and the
                        environment
                    </li>
                    <li>
                        Leveraging automation to minimise day-to-day management
                        tasks
                    </li>
                    <li>
                        Delivering affordable and reliable nationwide travel for
                        our customers, with consistently exceptional customer
                        service
                    </li>
                    <li>
                        Intelligently applying modern and fluid advertising
                        campaigns
                    </li>
                </ul>
            </SplitBlock>

            <SplitBlock
                id='team-operations'
                path='hero-1'
                alt='Airport Arrivals Traveler'
                className='bg-white dark:bg-neutral-900'
            >
                <SplitBlockHeader>Our Operations Team</SplitBlockHeader>
                <p>
                    Day-to-day customer sales and support activities are handled
                    by our UK operations team; a flexible combination of on-site
                    and remote-working staff. Tasks are varied, but the team's
                    primary goal is satisfying our customers' travel
                    requirements and solving any complications that may arise,
                    using the tools made available by our development team.
                </p>
                <p>
                    See our{' '}
                    <a
                        href='https://uk.indeed.com/cmp/Onward-Travel-Solutions-Ltd/jobs?q=Customer Service'
                        title='View Operational Careers with Onward Travel Solutions'
                        target='_blank'
                    >
                        Careers Listings
                    </a>{' '}
                    for available operations roles, or contact us below.
                </p>
            </SplitBlock>

            <SplitBlock
                id='team-development'
                path='hero-6'
                alt='Onward Travel Solutions Software Development'
                className='[&>div]:md:flex-row-reverse'
            >
                <SplitBlockHeader>Our Development Team</SplitBlockHeader>
                <p>
                    Our experienced in-house development team have dedicated
                    (but adjacent) offices, buffered from customer and driver
                    interaction by the operations team. This allows our
                    developers to focus on long-term product development and
                    maintenance of established products; while also having
                    convenient access to product stakeholders and users for
                    rapid iterative development.
                </p>
                <p>
                    See our{' '}
                    <a
                        href='https://uk.indeed.com/cmp/Onward-Travel-Solutions-Ltd/jobs?q=Developer'
                        title='View Development Careers with Onward Travel Solutions'
                        target='_blank'
                    >
                        Careers Listings
                    </a>{' '}
                    for available development roles, or contact us below.
                </p>
            </SplitBlock>

            <Section
                className='bg-white dark:bg-neutral-900 py-20 lg:py-38'
                id='technology'
            >
                <div className='m-auto prose-h2:text-3xl prose-h2:lg:text-4xl text-center prose-headings:uppercase prose'>
                    <h2 className='mb-6'>Our Products</h2>
                    <Rule className='m-auto w-1/2 max-w-60' />
                </div>
                <div className='gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-[3em]'>
                    {products.map((product, i) => (
                        <Card
                            id={`product-${i}`}
                            key={`product-${i}`}
                            className='flex flex-col justify-between space-y-8 bg-gray-100 dark:bg-neutral-800 dark:border dark:border-neutral-500 dark:border-solid text-left'
                        >
                            <div className='flex justify-between gap-5'>
                                <h3 className='font-semibold text-secondary text-xl'>
                                    {product.title}
                                </h3>
                                {product.image && (
                                    <img
                                        src={product.image.src}
                                        alt={
                                            product.image.alt ??
                                            `${product.title} Image`
                                        }
                                        className={`size-20 ${product.image.className ?? ''}`}
                                    />
                                )}
                            </div>
                            <p className='font-light text-slate-700 dark:text-gray-300'>
                                {product.content}
                            </p>
                        </Card>
                    ))}
                </div>
            </Section>

            <SplitBlock
                path='private-hire-jobs-call-centre'
                alt='Onward Travel Solutions Ltd Vision'
            >
                <Quote author='Nigel Beer, Managing Director'>
                    <p>
                        Since launching Onward Travel Solutions in 2006, our
                        mission has always been to provide UK airport transfers
                        and exceptional service at affordable prices.
                    </p>
                    <p>
                        Our ongoing investment in people, technology, innovation
                        and automation has allowed us to seamlessly and
                        efficiently connect travellers and transfer companies;
                        all backed up by fantastic 24/7 customer support.
                    </p>
                </Quote>
            </SplitBlock>

            <Section
                className='bg-white dark:bg-neutral-900 py-15 lg:py-20'
                id='trustpilot-reviews'
            >
                <TrustpilotReviews theme={isDarkMode() ? 'dark' : 'light'} />
            </Section>

            <Section
                className='bg-gray-100 dark:bg-neutral-800 py-20 lg:py-38'
                id='contact-us'
            >
                <div className='m-auto mb-14 max-w-3xl text-base lg:text-lg prose-h2:text-3xl prose-h2:lg:text-4xl text-center prose-headings:uppercase prose'>
                    <h2 className='mb-6'>Get In Touch</h2>
                    <Rule className='m-auto mb-6 w-1/2 max-w-60' />
                    <p>
                        If you are interested in working with us, wish to
                        discuss technological integrations, or have any other
                        queries, please get in contact using the form below. If
                        you are a driver and wish to know more{' '}
                        <Link href='/drivers' title="View the Driver's page">
                            please visit the Driver's page
                        </Link>
                        .
                    </p>
                </div>
                <NoticeErrorBoundary>
                    <ContactForm sites={sites} recaptcha={recaptcha} />
                </NoticeErrorBoundary>
            </Section>

            <Section
                className='[&>div]:gap-8 [&>div]:grid [&>div]:lg:grid-cols-3 bg-white dark:bg-transparent py-20 dark:[&>div>div]:border'
                id='testimonials'
            >
                {testimonials.map((testimonial, i) => (
                    <TestimonialCard
                        id={`testimonial-${i}`}
                        key={`testimonial-${i}`}
                        {...testimonial}
                    />
                ))}
            </Section>
        </>
    );
}
